import { DownOutlined } from '@ant-design/icons';
import { Alert, Button, Dropdown, Space, Tooltip } from 'antd';
import { useSearchParams } from 'react-router-dom';

import { CandidateCollection, JobPostingDetail } from '../../types';

interface ApplicantAnalysisBulkActionsProps {
  candidateIds: CandidateCollection['id'][];
  isHiredStage?: boolean;
  jobPostingStatus?: JobPostingDetail['status'];
  enableAlchemerIntegration?: boolean;
  questionsCount?: number;
  allCandidatesWhoAnsweredQuestions?: CandidateCollection['id'][];
  sendPracticalTechnicalCase?: boolean;
  onMoveStageClick?: () => void;
  onRejectClick?: () => void;
  onSendEmailClick?: () => void;
  onSendTestsClick?: () => void;
  onSendCustomTestsClick?: () => void;
  onSendAdditionalQuestionsClick?: () => void;
  onSendPracticalTechnicalCase?: () => void;
  onSelectClick?: () => void;
  onClear?: () => void;
}

const ApplicantAnalysisBulkActions = ({
  isHiredStage,
  candidateIds,
  jobPostingStatus,
  enableAlchemerIntegration,
  questionsCount,
  allCandidatesWhoAnsweredQuestions,
  sendPracticalTechnicalCase,
  onMoveStageClick,
  onRejectClick,
  onSendAdditionalQuestionsClick,
  onSendCustomTestsClick,
  onSendEmailClick,
  onSendTestsClick,
  onSendPracticalTechnicalCase,
  onSelectClick,
  onClear,
}: ApplicantAnalysisBulkActionsProps) => {
  const [searchParams] = useSearchParams();

  const candidatesWhoAnswered = allCandidatesWhoAnsweredQuestions?.filter((candidate) =>
    candidateIds.includes(candidate)
  );

  return (
    <Alert
      message={`Você selecionou ${candidateIds.length} candidato(s)`}
      action={
        <Space>
          {!isHiredStage && (
            <>
              {!['INACTIVE', 'GIVEN_UP'].includes(searchParams.get('status') || '') && (
                <>
                  {jobPostingStatus === 'IN_PROGRESS' && (
                    <Button onClick={onMoveStageClick}>Mover de etapa</Button>
                  )}
                  <Button onClick={onRejectClick}>Reprovar</Button>
                </>
              )}
            </>
          )}
          <Dropdown
            menu={{
              onClick: ({ key }) => {
                switch (key) {
                  case 'email':
                    onSendEmailClick?.();
                    break;
                  case 'tests':
                    onSendTestsClick?.();
                    break;
                  case 'custom-tests':
                    onSendCustomTestsClick?.();
                    break;
                  case 'additional_questions':
                    onSendAdditionalQuestionsClick?.();
                    break;
                  case 'practical-technical-case':
                    onSendPracticalTechnicalCase?.();
                    break;
                }
              },
              items: [
                { key: 'email', label: 'Enviar comunicação' },
                { key: 'tests', label: 'Enviar testes' },
                ...(enableAlchemerIntegration
                  ? [{ key: 'custom-tests', label: 'Enviar testes customizados' }]
                  : []),
                ...(questionsCount
                  ? [
                      {
                        key: 'additional_questions',
                        disabled:
                          candidatesWhoAnswered &&
                          candidatesWhoAnswered.length === candidateIds.length,
                        label: (
                          <Tooltip
                            title={
                              candidatesWhoAnswered &&
                              candidatesWhoAnswered.length === candidateIds.length
                                ? 'Os candidatos já responderam as perguntas de pré-requisito'
                                : ''
                            }
                          >
                            Enviar perguntas de pré-requisito
                          </Tooltip>
                        ),
                      },
                    ]
                  : []),
                ...(sendPracticalTechnicalCase
                  ? [{ key: 'practical-technical-case', label: 'Enviar case prático ou técnico' }]
                  : []),
              ],
            }}
          >
            <Button data-testid="bulk-actions-send-email">
              Enviar e-mail <DownOutlined />
            </Button>
          </Dropdown>
          <Button onClick={onSelectClick}>Adicionar à vaga</Button>
          <Button type="link" onClick={onClear}>
            Limpar
          </Button>
        </Space>
      }
    />
  );
};

export default ApplicantAnalysisBulkActions;
